import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  MenuItem,
  Box,
  styled,
  Typography as MuiTypography,
  Select,
  Button,
} from '@mui/material';
import { DateTime } from 'luxon';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

import { PassengerPhone } from '../../../data/models/passenger.model';
import {
  Reservation,
  ReservationModelState,
  TripLegAddress,
  TripLegType,
} from '../../../data/models/reservation.model';
import {
  TripLegStatus,
  tripStatusWithEtaList,
} from '../../../data/models/trip-leg-status.model';
import { TransportReservation } from '@/data/models/transport-reservation.model';

import dateUtils from '../../../services/DateUtils';
import reservationUtils from '../../../services/reservationUtils';
import { phoneUtils } from '../../../services/phoneUtils';

import * as accountingSlice from '@/data/accounting.slice';
import * as passengerSlice from '../../../data/passenger.slice';
import * as clientSlice from '../../../data/client.slice';
import * as providerSlice from '../../../data/provider.slice';
import * as tripLegSlice from '../../../data/tripleg.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import * as driverSlice from '../../../data/driver.slice';
import {
  fetchAllReservationsAsync,
  selectAllReservations,
} from '@/data/slices/reservation.slice';
import { mapTransportTripLeg } from '@/services/transportReservationMapper';

import TripLegDialog from '../../home/reservations/TripLegDialog';
import ReturnLink from '../../controls/ReturnLink';
import MemberTripLegStatusCard from '../reservation/MemberTripLegStatusCard';
import MemberTripLegLiveTracking from '../reservation/MemberTripLegLiveTracking';

const PREFIX = 'TripDetailsPage';

const classes = {
  container: `${PREFIX}-container`,
  navContainer: `${PREFIX}-navContainer`,
  header: `${PREFIX}-header`,
  body: `${PREFIX}-body`,
  row: `${PREFIX}-row`,
  title: `${PREFIX}-title`,
  mobileTripNumber: `${PREFIX}-mobileTripNumber`,
  legActions: `${PREFIX}-leg-actions`,
  legSelect: `${PREFIX}-leg-select`,
  legSelectRoot: `${PREFIX}-leg-select-root`,
  legSelectInput: `${PREFIX}-leg-select-input`,
  legSelectMenu: `${PREFIX}-leg-select-menu`,
  legSelectMenuItemSuccess: `${PREFIX}-leg-select-menu-item-success`,
  cardContent: `${PREFIX}-cardContent`,
  cardHeaderContainer: `${PREFIX}-cardHeaderContainer`,
  cardHeaderTitleContainer: `${PREFIX}-cardHeaderTitleContainer`,
  cardBodyContainer: `${PREFIX}-cardBodyContainer`,
  cardHeaderTitle: `${PREFIX}-cardHeaderTitle`,
  cardHeaderSubTitle: `${PREFIX}-cardHeaderSubTitle`,
  mapContainer: `${PREFIX}-mapContainer`,
  tripCard: `${PREFIX}-tripCard`,
  tripContainer: `${PREFIX}-tripContainer`,
  tripItem: `${PREFIX}-tripItem`,
  tripItemConnector: `${PREFIX}-tripItemConnector`,
  tripItemText: `${PREFIX}-tripItemText`,
  tripNoteChip: `${PREFIX}-trip-note-chip`,
  tripDestinations: `${PREFIX}-tripDestinations`,
  textSecondary: `${PREFIX}-textSecondary`,
  textSuccess: `${PREFIX}-textSuccess`,
  infoIcon: `${PREFIX}-infoIcon`,
};

const TripMarkerIcon = styled('img')(() => ({
  width: 24,
  height: 24,
}));

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    backgroundImage: 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    minHeight: 'calc(100vh - 161px - 23px)',
    [theme.breakpoints.down('lg')]: {
      height: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      justifyContent: 'space-between',
      padding: 0,
    },
  },

  [`&.${classes.navContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
  },

  [`&.${classes.title}`]: {
    fontSize: '21px',
  },

  [`&.${classes.header}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0),
    marginTop: theme.spacing(5.875),
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
    '& .prev-link': {
      textDecoration: 'none',
    },
  },

  [`&.${classes.mobileTripNumber}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0),
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },

  [`&.${classes.row}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },

  [`&.${classes.legActions}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    '& button': {
      height: 48,
      minWidth: 144,
    },
  },

  [`& .${classes.legSelect}`]: {
    height: '48px',
    '& .MuiSelect-select': {
      padding: theme.spacing(1.5),
      paddingRight: 40,
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiSelect-icon': {
      color: theme.palette.text.secondary,
      top: '50%',
      transform: 'translateY(-50%)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },

  [`& .${classes.legSelectRoot}`]: {
    padding: 0,
  },

  [`& .${classes.legSelectInput}`]: {
    padding: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
  },

  [`& .${classes.legSelectMenuItemSuccess}`]: {
    color: theme.palette.success.main,
  },

  [`& .MuiSvgIcon-root`]: {
    fontSize: '24px',
    verticalAlign: 'middle',
  },

  [`&.${classes.body}`]: {
    display: 'flex',
    width: '100%',
    gap: theme.spacing(3),
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column-reverse',
      flexGrow: 1,
      justifyContent: 'end',
      gap: 0,
    },
  },

  [`&.${classes.cardContent}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },

  [`&.${classes.cardHeaderContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%',
    marginBottom: theme.spacing(2),
  },

  [`&.${classes.cardHeaderTitleContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  [`&.${classes.cardBodyContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    '& .MuiTypography-root': {
      fontSize: theme.typography.body2.fontSize,
    },
  },

  [`&.${classes.mapContainer}`]: {
    flex: '1',
    display: 'flex',
    height: 'max-content',
    minHeight: 465,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    overflow: 'hidden',
    [theme.breakpoints.down('lg')]: {
      boxShadow: 'none',
      minHeight: 265,
      borderRadius: 0,
    },
    '& .MemberTripLegLiveTracking-container': {
      display: '-webkit-box',
    },
  },

  [`&.${classes.tripContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },

  [`&.${classes.tripItem}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1.5),
    [`& svg`]: {
      backgroundColor: theme.palette.background.default,
    },
  },

  [`&.${classes.tripNoteChip}`]: {
    color: theme.palette.primary.dark,
    fontSize: '13px',
    fontWeight: 500,
    borderRadius: '16px',
    backgroundColor: 'rgba(25, 124, 179, 0.12)',
    width: 'max-content',
    marginTop: theme.spacing(1),
    padding: `${theme.spacing(0.375)} ${theme.spacing(1.25)}`,
  },

  [`&.${classes.tripDestinations}`]: {
    '& > :first-of-type': {
      paddingBottom: 40,
    },
  },

  [`&.${classes.tripItemConnector}`]: {
    width: theme.spacing(0.5),
    height: '100%',
    margin: '0 auto',
    backgroundImage: "url('/dot-small.svg')",
    paddingBottom: theme.spacing(1),
  },

  [`&.${classes.tripItemText}`]: {
    display: 'flex',
    flexDirection: 'column',
  },

  [`&.${classes.infoIcon}`]: {
    color: theme.palette.text.secondary,
  },
}));

const Typography = styled(MuiTypography)(({ theme }) => ({
  [`&.${classes.cardHeaderTitle}`]: {
    fontSize: '21px',
    padding: 0,
  },

  [`&.${classes.cardHeaderSubTitle}`]: {
    fontSize: theme.typography.body2.fontSize,
  },

  [`&.${classes.textSecondary}`]: {
    color: theme.palette.lightTextSecondary.main,
  },

  [`&.${classes.textSuccess}`]: {
    color: theme.palette.success.main,
  },
}));

const TripDetailsPage = () => {
  const { t } = useTranslation('common');
  const { referenceId, referenceLegIndex } = useParams();
  const [searchParams] = useSearchParams();
  const fromTripsPage = searchParams.get('fromTripsPage') === 'true';
  const dispatch = useAppDispatch();
  const passenger = useAppSelector(passengerSlice.selectCurrentPassenger);
  const driverLocationsByTripLegReferenceId = useAppSelector(
    driverSlice.selectDriverLocationsByTripLegReferenceId,
  );
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const POLLING_INTERVAL = 90000;
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const clientData = useAppSelector(clientSlice.selectPassengerClientData);
  const allReservations = useAppSelector(selectAllReservations);
  const provider = useAppSelector(providerSlice.selectProvider);
  const providerName = provider?.transportationProviderName || '--';
  const syncedTripLegs = useAppSelector(tripLegSlice.selectTripLegDetails);
  const tripModes = useAppSelector(lookupSlice.selectTripModes);
  const passengerNoteTypes = useAppSelector(
    lookupSlice.selectPassengerNoteTypes,
  );
  const tripLegEvent = useAppSelector(tripLegSlice.selectTripLegEventEmitted);
  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );

  const [legIndex, setLegIndex] = useState(0);
  const [requestedProvider, setRequestedProvider] = useState(false);

  const reservation = useMemo(() => {
    let reservation: TransportReservation | undefined;
    reservation = allReservations?.find(
      (res) => res.reservationReferenceID === referenceId,
    );
    return reservation;
  }, [allReservations, referenceId]);

  const isReservationActive =
    reservation?.reservationState === ReservationModelState.active;

  const tripLeg = useMemo(
    () => reservation?.tripLegs[legIndex],
    [reservation, legIndex],
  );

  const syncedTripLeg = useMemo(() => {
    if (!tripLeg?.tripLegReferenceID) return null;
    return syncedTripLegs[tripLeg.tripLegReferenceID] || null;
  }, [tripLeg, syncedTripLegs]);

  // TripLegDialog still expects the old F1Cloud schema
  const tripLegForDialog = useMemo(
    () =>
      tripLeg && transportationAttributes?.length
        ? mapTransportTripLeg(
            tripLeg,
            reservation ?? null,
            transportationAttributes,
          )
        : undefined,
    [tripLeg, transportationAttributes, reservation?.passenger],
  );

  const isTripLegLive = useMemo(() => {
    return (
      tripLeg?.tripLegStatusBC &&
      tripStatusWithEtaList.includes(
        tripLeg.tripLegStatusBC.toLocaleLowerCase() as TripLegStatus,
      )
    );
  }, [tripLeg]);

  const hasMultipleLegs = (reservation?.tripLegs?.length || 0) > 1;

  const driverLocationForTrip = useMemo(() => {
    if (!tripLeg?.tripLegReferenceID) return null;
    return (
      driverLocationsByTripLegReferenceId[tripLeg.tripLegReferenceID] || null
    );
  }, [tripLeg?.tripLegReferenceID, driverLocationsByTripLegReferenceId]);

  const driverEtaSeconds = useMemo(() => {
    if (driverLocationForTrip) {
      return (
        driverLocationForTrip.estimatedTimeOfArrivalSeconds ??
        driverLocationForTrip.eta
      );
    }

    const locations = syncedTripLeg?.tripLegDriverLocations;
    if (locations && locations.length > 0) {
      const lastLocation = locations[locations.length - 1];
      return lastLocation?.estimatedTimeOfArrivalSeconds;
    }

    return undefined;
  }, [driverLocationForTrip, syncedTripLeg?.tripLegDriverLocations]);

  const driverName = reservationUtils.getDriverName(syncedTripLeg?.driver);
  const vehicleName = syncedTripLeg?.vehicle
    ? [
        syncedTripLeg.vehicle.vehicleColor,
        syncedTripLeg.vehicle.vehicleMake,
        syncedTripLeg.vehicle.vehicleModel,
      ]
        .filter(Boolean)
        .join(' ') +
        (syncedTripLeg.vehicle.licensePlate
          ? ` (${syncedTripLeg.vehicle.licensePlate})`
          : '') || undefined
    : undefined;

  const tripLegPurpose = useMemo(() => {
    return syncedTripLeg?.clientPurposeCode?.purposeCodeName || '--';
  }, [syncedTripLeg]);

  useEffect(() => {
    const driverId = syncedTripLeg?.driverID;
    const fetchDriverLocation = () => {
      dispatch(driverSlice.fetchDriverLocationAsync(Number(driverId)));
    };

    if (isTripLegLive && driverId) {
      fetchDriverLocation();
      timerIntervalRef.current = setInterval(
        fetchDriverLocation,
        POLLING_INTERVAL,
      );
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isTripLegLive, syncedTripLeg?.driverID]);

  const passengerNotes = useMemo(() => {
    return (
      tripLeg?.tripLegPassengerNotes
        ?.map(
          (note) =>
            passengerNoteTypes.find(
              (nt) =>
                nt.tripLegPassengerNoteTypeBC ===
                note.tripLegPassengerNoteTypeBC,
            )?.tripLegPassengerNoteTypeName || '',
        )
        .filter(Boolean) || []
    );
  }, [tripLeg, passengerNoteTypes]);

  const fetchFutureReservations = () => {
    if (!passenger) return;
    dispatch(
      fetchAllReservationsAsync({
        passengerUUIDs: [passenger.passengerUUID!],
        page: 1,
        pageSize: 100,
        includePassenger: true,
        includeTripLegs: true,
        sort: 'reservationServiceDate',
        excludeCanceled: true,
        reservationState: 'active',
      }),
    );
  };
  const fetchPastReservations = () => {
    if (!passenger) return;
    dispatch(
      fetchAllReservationsAsync({
        passengerUUIDs: [passenger.passengerUUID!],
        page: 1,
        pageSize: 100,
        includePassenger: true,
        includeTripLegs: true,
        sort: 'reservationServiceDate',
        excludeCanceled: true,
        reservationState: 'inactive',
      }),
    );
  };

  useEffect(() => {
    if (reservation?.tripLegs) {
      reservation.tripLegs.forEach((tl) => {
        if (!syncedTripLegs[tl.tripLegReferenceID!]) {
          dispatch(tripLegSlice.getTripLegDetailsAsync(tl.tripLegReferenceID!));
        }
      });

      if (referenceLegIndex) {
        setLegIndex(Number(referenceLegIndex));
      }
    }
  }, [reservation]);

  useEffect(() => {
    dispatch(driverSlice.clearDriverLocation());

    if (!passenger) return;
    if (isEditDialogOpen) return;
    dispatch(
      clientSlice.fetchPassengerClientDataAsync(
        passenger.clientBC,
        passenger.servicePlans,
      ),
    );
    dispatch(lookupSlice.fetchLookupItemsAsync('tripLegPassengerNoteType'));
    dispatch(lookupSlice.fetchLookupItemsAsync('tripMode'));
    dispatch(accountingSlice.fetchTransportationAttributesAsync());

    if (passenger.passengerUUID) {
      fetchFutureReservations();
      fetchPastReservations();
    }
  }, [passenger]);

  useEffect(() => {
    if (isEditDialogOpen) return;
    if (tripLegEvent && passenger?.passengerUUID) {
      fetchFutureReservations();
      fetchPastReservations();
      if (tripLegEvent.detail.tripLegReferenceID) {
        dispatch(
          tripLegSlice.getTripLegDetailsAsync(
            tripLegEvent.detail.tripLegReferenceID,
          ),
        );
      }
      dispatch(tripLegSlice.setTripLegEventEmitted(null));
    }
  }, [tripLegEvent, passenger?.passengerUUID, dispatch]);

  useEffect(() => {
    const providerID = tripLeg?.transportationProviderID;
    if (providerID) {
      dispatch(providerSlice.fetchProviderAsync(providerID));
    }
  }, [tripLeg?.transportationProviderID]);

  const retrievePhoneNumber = (phone?: string): string => {
    if (!phone) return '--';
    return phoneUtils.formatPhoneString(phone);
  };

  const retrievePickupTime = (
    pickupTimeUTC?: string,
    timeZone?: string,
  ): string => {
    if (!pickupTimeUTC || !timeZone) return '--';
    return dateUtils.formatAppointmentTime(pickupTimeUTC, timeZone);
  };

  const retrieveAppointmentDate = (
    dateTimeString?: string,
    timeZone?: string,
    dateString?: string,
  ): string => {
    if (!timeZone) return '--';
    if (dateTimeString) {
      const date = DateTime.fromISO(dateTimeString, { zone: timeZone });
      const time = dateUtils.formatAppointmentTime(dateTimeString, timeZone);
      return `${date.toFormat('MMMM dd, yyyy')} - ${time}`;
    }
    if (dateString) {
      return DateTime.fromISO(dateString, { zone: timeZone }).toFormat(
        'MMMM dd, yyyy',
      );
    }
    return '--';
  };

  const retrieveAddressString = (address?: TripLegAddress): string => {
    if (!address) return '--';
    const parts = [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.stateCode,
      address.zipCode,
    ];
    return parts.filter(Boolean).join(', ');
  };

  const tripMode = useMemo(() => {
    if (!syncedTripLeg) return '--';
    return (
      tripModes.find((tm) => tm.tripModeID === syncedTripLeg.tripModeID)
        ?.tripModeName ?? '--'
    );
  }, [syncedTripLeg, tripModes]);

  const handleEdit = () => {
    setIsEditDialogOpen(true);
  };

  return (
    <StyledBox className={classes.container}>
      <StyledBox className={classes.header}>
        <ReturnLink
          returnLink={fromTripsPage ? '/trips' : '/'}
          returnLinkText={fromTripsPage ? t('member.trips') : t('member.home')}
        />
        <StyledBox className={classes.row}>
          <Typography variant="h2">
            {`${t('member.trip')} #`}
            {tripLeg?.tripLegReferenceID || '--'}
          </Typography>
          <StyledBox className={classes.legActions}>
            <Button
              variant="contained"
              onClick={handleEdit}
              data-fs-element="EditTripButton">
              {t('common.edit')}
            </Button>
            {hasMultipleLegs && (
              <Select
                value={legIndex.toString()}
                onChange={(e) => setLegIndex(Number(e.target.value))}
                className={classes.legSelect}
                IconComponent={ExpandMoreIcon}
                inputProps={{
                  classes: {
                    root: classes.legSelectRoot,
                    select: classes.legSelectInput,
                  },
                }}>
                {reservation &&
                  reservation.tripLegs.map((_, index) => (
                    <MenuItem key={index} value={index.toString()}>
                      <Trans
                        i18nKey="member.legLabel"
                        values={{
                          current: index + 1,
                          total: reservation.tripLegs.length,
                        }}
                        components={{
                          status:
                            isReservationActive &&
                            (reservation.tripLegs[
                              index
                            ]?.tripLegStatusBC?.toLocaleLowerCase() as TripLegStatus) &&
                            tripStatusWithEtaList.includes(
                              reservation.tripLegs[
                                index
                              ].tripLegStatusBC.toLocaleLowerCase() as TripLegStatus,
                            ) ? (
                              <span
                                className={classes.legSelectMenuItemSuccess}>
                                &nbsp;({t(`member.active`).toLocaleLowerCase()}
                                )&nbsp;
                              </span>
                            ) : (
                              <></>
                            ),
                        }}
                      />
                    </MenuItem>
                  ))}
              </Select>
            )}
          </StyledBox>
        </StyledBox>
      </StyledBox>

      <StyledBox className={classes.body}>
        <Card className={classes.tripCard}>
          <CardContent>
            <StyledBox className={classes.cardContent}>
              <StyledBox className={classes.cardHeaderContainer}>
                <StyledBox className={classes.cardHeaderTitleContainer}>
                  <Typography className={classes.cardHeaderTitle} variant="h3">
                    {t('member.tripSummary')}
                  </Typography>
                </StyledBox>
                <StyledBox className={classes.mobileTripNumber}>
                  <StyledBox className={classes.tripItemText}>
                    <Typography
                      className={classes.textSecondary}
                      variant="subtitle2">
                      {`${t('member.trip')} #`}
                      {tripLeg?.tripLegReferenceID || '--'}
                    </Typography>
                  </StyledBox>
                </StyledBox>
              </StyledBox>
              {isReservationActive && isTripLegLive && (
                <MemberTripLegStatusCard
                  tripLegStatus={
                    tripLeg?.tripLegStatusBC.toLocaleLowerCase() as TripLegStatus
                  }
                  eta={driverEtaSeconds}
                  driverName={driverName}
                  vehicleName={vehicleName}
                />
              )}
              <StyledBox className={classes.cardBodyContainer}>
                {/* Passenger Info */}
                <StyledBox className={classes.tripItem}>
                  <PersonOutlineRoundedIcon color="info" />
                  <StyledBox className={classes.tripItemText}>
                    <Typography variant="subtitle2">
                      {passenger?.firstName} {passenger?.lastName}
                    </Typography>
                    {passenger && passenger.passengerPhones && (
                      <Typography className={classes.textSecondary}>
                        {retrievePhoneNumber(
                          passenger.passengerPhones[0]?.phoneNumber ?? '',
                        )}
                      </Typography>
                    )}
                    {passengerNotes.length > 0 && (
                      <StyledBox className={classes.tripNoteChip}>
                        {passengerNotes.map((note, idx) => (
                          <Typography key={idx}>{note}</Typography>
                        ))}
                      </StyledBox>
                    )}
                  </StyledBox>
                </StyledBox>

                {/* Appointment Info */}
                <StyledBox className={classes.tripItem}>
                  <DateRangeIcon color="info" />
                  <StyledBox className={classes.tripItemText}>
                    <Typography variant="body1">
                      {`${t('member.appointment')}: `}
                      <StyledBox component="span" className="bold">
                        {retrieveAppointmentDate(
                          tripLeg?.appointmentTimeUTC ?? undefined,
                          tripLeg?.appointmentTimeZoneIANA,
                          tripLeg?.appointmentDate,
                        )}
                      </StyledBox>
                    </Typography>
                    <Typography
                      variant="body2"
                      className={classes.textSecondary}>
                      {`${t('member.reasonForTrip')}: `}
                      <StyledBox component="span" className="bold">
                        {tripLegPurpose}
                      </StyledBox>
                    </Typography>
                  </StyledBox>
                </StyledBox>

                {/* Pickup Time */}
                <StyledBox className={classes.tripItem}>
                  <AccessTimeRoundedIcon color="info" />
                  <Typography variant="body1">
                    {tripLeg?.pickUpDueTimeUTC ? (
                      <>
                        {`${t('member.pickup')}: `}
                        <StyledBox component="span" className="bold">
                          {retrievePickupTime(
                            tripLeg.pickUpDueTimeUTC,
                            tripLeg?.pickUpAddressTimeZoneIANA,
                          )}
                        </StyledBox>
                      </>
                    ) : (
                      <StyledBox component="span" className="bold">
                        {t('member.pickupWillCall')}
                      </StyledBox>
                    )}
                  </Typography>
                </StyledBox>

                {/* Leg Addresses */}
                <StyledBox className={classes.cardContent}>
                  <StyledBox className={classes.tripContainer}>
                    <StyledBox className={classes.tripDestinations}>
                      <StyledBox className={classes.tripItem}>
                        <StyledBox>
                          <TripMarkerIcon
                            src="/pickup_marker_card.svg"
                            alt=""
                            role="presentation"
                          />
                          <StyledBox
                            className={classes.tripItemConnector}></StyledBox>
                        </StyledBox>
                        <StyledBox className={classes.tripItemText}>
                          <Typography variant="body1">
                            {`${t('common.from')}: `}
                            <StyledBox component="span" className="bold">
                              {retrieveAddressString({
                                addressLine1: tripLeg?.pickUpAddressLine1 ?? '',
                                addressLine2: tripLeg?.pickUpAddressLine2 ?? '',
                                city: tripLeg?.pickUpCity ?? '',
                                stateCode: tripLeg?.pickUpStateCode ?? '',
                                zipCode: tripLeg?.pickUpZipCode ?? '',
                                county: tripLeg?.pickUpCounty ?? '',
                              })}
                            </StyledBox>
                          </Typography>
                          {tripLeg?.pickUpPassengerPhoneNumber && (
                            <Typography
                              variant="body2"
                              className={classes.textSecondary}>
                              {retrievePhoneNumber(
                                tripLeg?.pickUpPassengerPhoneNumber,
                              )}
                            </Typography>
                          )}
                          {tripLeg?.pickUpNotes && (
                            <StyledBox className={classes.tripNoteChip}>
                              <Typography>{tripLeg.pickUpNotes}</Typography>
                            </StyledBox>
                          )}
                        </StyledBox>
                      </StyledBox>
                      <StyledBox className={classes.tripItem}>
                        <TripMarkerIcon
                          src="/dropoff_marker_card.svg"
                          alt=""
                          role="presentation"
                        />
                        <StyledBox className={classes.tripItemText}>
                          <Typography variant="body1">
                            {`${t('common.to')}: `}
                            <StyledBox component="span" className="bold">
                              {retrieveAddressString({
                                addressLine1:
                                  tripLeg?.dropOffAddressLine1 ?? '',
                                addressLine2:
                                  tripLeg?.dropOffAddressLine2 ?? '',
                                city: tripLeg?.dropOffCity ?? '',
                                stateCode: tripLeg?.dropOffStateCode ?? '',
                                zipCode: tripLeg?.dropOffZipCode ?? '',
                                county: tripLeg?.dropOffCounty ?? '',
                              })}
                            </StyledBox>
                          </Typography>
                          {tripLeg?.dropOffPassengerPhoneNumber && (
                            <Typography
                              variant="body2"
                              className={classes.textSecondary}>
                              {retrievePhoneNumber(
                                tripLeg?.dropOffPassengerPhoneNumber,
                              )}
                            </Typography>
                          )}
                          {tripLeg?.dropOffNotes && (
                            <StyledBox className={classes.tripNoteChip}>
                              <Typography>{tripLeg.dropOffNotes}</Typography>
                            </StyledBox>
                          )}
                        </StyledBox>
                      </StyledBox>
                    </StyledBox>
                  </StyledBox>
                </StyledBox>

                <StyledBox className={classes.tripItem}>
                  <DirectionsCarFilledOutlinedIcon color="info" />
                  <StyledBox className={classes.tripItemText}>
                    <Typography variant="body1">
                      {`${t('tripStatusTrack.tripMode')}: `}
                      <StyledBox component="span" className="bold">
                        {tripMode}
                      </StyledBox>
                    </Typography>

                    {provider?.transportationProviderName && (
                      <Typography variant="body1">
                        {`${t('tripStatusTrack.provider')}: `}
                        <StyledBox component="span" className="bold">
                          {provider.transportationProviderName}
                        </StyledBox>
                        <StyledBox component="span">
                          {`${requestedProvider ? ` (${t('member.requested')})` : ''}`}
                        </StyledBox>
                      </Typography>
                    )}
                  </StyledBox>
                </StyledBox>
              </StyledBox>
            </StyledBox>
          </CardContent>
        </Card>

        {tripLeg && (
          <StyledBox className={classes.mapContainer}>
            <MemberTripLegLiveTracking tripLeg={tripLeg} />
          </StyledBox>
        )}
      </StyledBox>
      {tripLegForDialog && passenger && referenceId && (
        <TripLegDialog
          sequence={tripLegForDialog.tripLegSequence}
          reservationReferenceID={referenceId}
          tripLeg={tripLegForDialog}
          tripType={TripLegType.MultiLeg}
          open={isEditDialogOpen}
          passenger={passenger}
          servicePlans={clientData.servicePlans}
          close={() => setIsEditDialogOpen(false)}
        />
      )}
    </StyledBox>
  );
};

export default TripDetailsPage;
